import type { Routes } from '@angular/router';

import { authMatchGuard, hasRoleGuard, loginRedirectGuard } from '@ebt/shared-auth';

/**
 * CRM internal app routes.
 *
 * `/login` is unguarded but redirects to `/` if already authenticated.
 * Everything else sits under the auth-gated app shell — `authMatchGuard`
 * runs at `canMatch` so unauthenticated visitors don't even download
 * the protected chunks. The shell carries the sidebar + topbar; routed
 * children render inside its `<router-outlet />`.
 *
 * The `/design-system` route stays open in dev so designers can review
 * primitives without an account; revisit before a public deploy.
 */
export const routes: Routes = [
  {
    path: 'login',
    title: 'Sign in · EBT CRM',
    canMatch: [loginRedirectGuard],
    loadComponent: () => import('./auth/login.page').then((m) => m.LoginPage),
  },
  {
    path: 'design-system',
    title: 'Design System · EBT CRM',
    loadComponent: () =>
      import('./design-system/design-system.page').then((m) => m.DesignSystemPage),
  },
  {
    path: 'reset-password',
    title: 'Reset password · EBT CRM',
    loadComponent: () => import('./auth/reset-password.page').then((m) => m.ResetPasswordPage),
  },
  {
    path: '',
    canMatch: [authMatchGuard],
    loadComponent: () => import('./shell/app-shell.component').then((m) => m.AppShellComponent),
    children: [
      {
        path: '',
        pathMatch: 'full',
        title: 'Dashboard · EBT CRM',
        loadComponent: () => import('./dashboard/dashboard.page').then((m) => m.DashboardPage),
      },
      {
        path: 'customers',
        title: 'Customers · EBT CRM',
        loadComponent: () =>
          import('./customers/customers-list.page').then((m) => m.CustomersListPage),
      },
      // EBT-127 — New / Edit Customer are full pages, not popups.
      // `customers/new` must stay above `customers/:id`.
      {
        path: 'customers/new',
        title: 'New Customer · EBT CRM',
        data: { mode: 'create' },
        loadComponent: () =>
          import('./customers/customer-form.page').then((m) => m.CustomerFormPage),
      },
      {
        path: 'customers/:id',
        title: 'Customer · EBT CRM',
        loadComponent: () =>
          import('./customers/customer-detail.page').then((m) => m.CustomerDetailPage),
      },
      {
        path: 'customers/:id/edit',
        title: 'Edit Customer · EBT CRM',
        data: { mode: 'edit' },
        loadComponent: () =>
          import('./customers/customer-form.page').then((m) => m.CustomerFormPage),
      },
      {
        path: 'leads',
        title: 'Leads · EBT CRM',
        loadComponent: () => import('./leads/leads-list.page').then((m) => m.LeadsListPage),
      },
      // EBT-122 Lead Workspace — one page for create / view / edit; the mode
      // comes from route data. `leads/new` must stay above `leads/:id`.
      {
        path: 'leads/new',
        title: 'New Lead · EBT CRM',
        data: { mode: 'create' },
        loadComponent: () => import('./leads/lead-detail.page').then((m) => m.LeadDetailPage),
      },
      {
        path: 'leads/:id',
        title: 'Lead · EBT CRM',
        data: { mode: 'view' },
        loadComponent: () => import('./leads/lead-detail.page').then((m) => m.LeadDetailPage),
      },
      {
        path: 'leads/:id/edit',
        title: 'Edit Lead · EBT CRM',
        data: { mode: 'edit' },
        loadComponent: () => import('./leads/lead-detail.page').then((m) => m.LeadDetailPage),
      },
      {
        path: 'quotes',
        title: 'Quotes · EBT CRM',
        loadComponent: () => import('./quotes/quotes-list.page').then((m) => m.QuotesListPage),
      },
      {
        path: 'quotes/new',
        title: 'New Quote · EBT CRM',
        loadComponent: () => import('./quotes/quote-new.page').then((m) => m.QuoteNewPage),
      },
      {
        path: 'quotes/:id',
        title: 'Quote · EBT CRM',
        loadComponent: () => import('./quotes/quote-detail.page').then((m) => m.QuoteDetailPage),
      },
      {
        // Editing reuses the New Quote builder, so a draft is edited with the
        // exact field set it was created with. The page itself redirects back
        // to `quotes/:id` for anything past `draft` — the API refuses header
        // edits outside that status.
        path: 'quotes/:id/edit',
        title: 'Edit Quote · EBT CRM',
        loadComponent: () => import('./quotes/quote-new.page').then((m) => m.QuoteNewPage),
      },
      {
        path: 'notifications',
        title: 'Notifications & Activity · EBT CRM',
        loadComponent: () =>
          import('./notifications/notifications.page').then((m) => m.NotificationsPage),
      },
      {
        path: 'jobs',
        pathMatch: 'full',
        redirectTo: 'jobs/board',
      },
      {
        path: 'runs',
        pathMatch: 'full',
        redirectTo: 'runs/planning',
      },
      {
        path: 'runs/planning',
        title: 'Runs Planning · EBT CRM',
        loadComponent: () =>
          import('./runs-planning/runs-planning.page').then((m) => m.RunsPlanningPage),
      },
      {
        path: 'run-types',
        title: 'Run Types · EBT CRM',
        loadComponent: () =>
          import('./runs-planning/run-types.page').then((m) => m.RunTypesPage),
      },
      {
        path: 'runs/:id',
        title: 'Run · EBT CRM',
        loadComponent: () =>
          import('./runs-planning/run-detail.page').then((m) => m.RunDetailPage),
      },
      {
        path: 'trucks',
        title: 'Trucks · EBT CRM',
        loadComponent: () =>
          import('./trucks/trucks-list.page').then((m) => m.TrucksListPage),
      },
      {
        path: 'trucks/:id',
        title: 'Truck · EBT CRM',
        loadComponent: () =>
          import('./trucks/truck-detail.page').then((m) => m.TruckDetailPage),
      },
      {
        path: 'drivers',
        title: 'Drivers · EBT CRM',
        loadComponent: () =>
          import('./drivers/drivers-list.page').then((m) => m.DriversListPage),
      },
      {
        path: 'drivers/:id',
        title: 'Driver · EBT CRM',
        loadComponent: () =>
          import('./drivers/driver-detail.page').then((m) => m.DriverDetailPage),
      },
      // Calendar hidden temporarily — redirect to dashboard.
      { path: 'calendar', pathMatch: 'prefix', redirectTo: '' },
      // {
      //   path: 'calendar',
      //   title: 'Schedule · EBT CRM',
      //   loadComponent: () =>
      //     import('./calendar/calendar.page').then((m) => m.CalendarPage),
      // },
      {
        path: 'jobs/new',
        title: 'New Job · EBT CRM',
        loadComponent: () => import('./jobs/job-create.page').then((m) => m.JobCreatePage),
      },
      {
        path: 'jobs/board',
        title: 'Job Board · EBT CRM',
        loadComponent: () => import('./jobs/job-board.page').then((m) => m.JobBoardPage),
      },
      {
        path: 'jobs/:id',
        title: 'Job · EBT CRM',
        loadComponent: () => import('./jobs/job-detail.page').then((m) => m.JobDetailPage),
      },
      // Reports hidden temporarily — redirect to dashboard.
      { path: 'reports', pathMatch: 'prefix', redirectTo: '' },
      // {
      //   path: 'reports',
      //   title: 'Reports · EBT CRM',
      //   loadComponent: () => import('./reports/reports.page').then((m) => m.ReportsPage),
      // },
      {
        path: 'settings',
        pathMatch: 'full',
        redirectTo: 'settings/site',
      },
      {
        path: 'settings/site',
        title: 'Site Settings · EBT CRM',
        loadComponent: () =>
          import('./settings/site-settings.page').then((m) => m.SiteSettingsPage),
      },
      // Hull Type / Trailer list admin. Office admin and master admin are the
      // roles that hold `admin:reference_data`, which is what the API's writes
      // require — the role guard here just turns a screen full of 403s into a
      // redirect for everyone else.
      {
        path: 'settings/lists',
        title: 'Boat & Trailer Lists · EBT CRM',
        canActivate: [hasRoleGuard('master_admin', 'office_admin')],
        loadComponent: () =>
          import('./settings/reference-lists.page').then((m) => m.ReferenceListsPage),
      },
      // User Management is for master admins and office admins. The guard
      // mirrors the `@RequireRoles('master_admin', 'office_admin')` on the
      // backend's /users management endpoints — without it, other roles reach
      // the screen and get a wall of 403s instead of a redirect. What an
      // office admin sees inside is narrowed by the backend, not here: master
      // admin accounts are filtered out of every response.
      {
        path: 'settings/users',
        title: 'User Management · EBT CRM',
        canActivate: [hasRoleGuard('master_admin', 'office_admin')],
        loadComponent: () => import('./users/users-list.page').then((m) => m.UsersListPage),
      },
      {
        path: 'settings/users/:id',
        title: 'User · EBT CRM',
        canActivate: [hasRoleGuard('master_admin', 'office_admin')],
        loadComponent: () => import('./users/user-detail.page').then((m) => m.UserDetailPage),
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
